import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export type NavigationGuardRegistration = {
  isDirty: boolean;
  requestNavigation: (next: () => void) => void;
};

type UnsavedNavigationGuardContextValue = {
  guard: NavigationGuardRegistration | null;
  registerGuard: (guard: NavigationGuardRegistration | null) => void;
};

const UnsavedNavigationGuardContext = createContext<UnsavedNavigationGuardContextValue>({
  guard: null,
  registerGuard: () => {},
});

export function UnsavedNavigationGuardProvider({ children }: { children: React.ReactNode }) {
  const [guard, setGuard] = useState<NavigationGuardRegistration | null>(null);
  const registerGuard = useCallback((nextGuard: NavigationGuardRegistration | null) => {
    setGuard(nextGuard);
  }, []);
  const value = useMemo(() => ({ guard, registerGuard }), [guard, registerGuard]);
  return <UnsavedNavigationGuardContext.Provider value={value}>{children}</UnsavedNavigationGuardContext.Provider>;
}

export function useUnsavedNavigationGuardContext() {
  return useContext(UnsavedNavigationGuardContext);
}

export function useRegisterNavigationGuard(registration: NavigationGuardRegistration | null) {
  const { registerGuard } = useUnsavedNavigationGuardContext();
  useEffect(() => {
    registerGuard(registration);
  }, [registerGuard, registration]);
  useEffect(() => {
    return () => {
      registerGuard(null);
    };
  }, [registerGuard]);
}
